import { useAccountStore } from '@/store/use-account-store';
import {
  getAllMarketStoreStates,
  useMarketStore,
} from '@/store/use-markets-store';
import { FilledOrder, useOrdersStore } from '@/store/use-orders-store';
import { OrderStatus } from '@/types';
import { bigIntToNum, formatBigInt } from '@/utils/value-format';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

type Props = {
  orderId: string;
};

export default function Price({ orderId }: Props) {
  const address = useAccountStore((state) => state.address);
  const { price, symbol } = useOrdersStore(
    useShallow((state) => {
      const order = state.getOrders(address).orders[orderId];
      if (
        order.status === OrderStatus.FILLED ||
        order.status === OrderStatus.PARTIALLY_FILLED
      ) {
        const { symbol, lastFilledPrice } = order;
        return { symbol, price: lastFilledPrice };
      }
      const { symbol, limitPrice } = order;
      return { symbol, price: limitPrice };
    }),
  );

  // NOTE: in case symbol is not listed
  const priceDecimals = useMemo(() => {
    const markets = getAllMarketStoreStates();
    if (!(symbol in markets)) return;
    return markets[symbol].marketSpec.priceDecimals;
  }, [symbol]);

  return priceDecimals
    ? formatBigInt(price, priceDecimals, {
        digits: Number(priceDecimals),
        style: 'currency',
      })
    : '-';
}

export const limitPriceAccessorFn = (id: string) => {
  const { address } = useAccountStore.getState();
  const { orders } = useOrdersStore.getState().getOrders(address);
  const order = orders[id];
  const { priceDecimals } = useMarketStore(order.symbol).getState().marketSpec;
  let price = 0n;
  if (
    order.status === OrderStatus.FILLED ||
    order.status === OrderStatus.PARTIALLY_FILLED
  ) {
    price = (order as FilledOrder).lastFilledPrice;
  } else {
    price = order.limitPrice;
  }
  return bigIntToNum(price, priceDecimals);
};
